import { useCallback } from 'react';

import { createUrlWithSourceQueryParams } from '@crehana/analytics';
import {
  CourseCard,
  type TInteractiveCourseCardProps,
} from '@crehana/compass.knowledgehub/components';
import type { TSession } from '@crehana/compass.live-sessions';
import { formatDate, isDateValid } from '@crehana/date';
import type { UserTeamItem } from '@crehana/web';

import { useLearnDashboardContext } from '@/views/LearnDashboard/context';
import type { UserTeamItemParsed } from '@/views/LearnDashboard/hooks/useTeamTracks/utils/useParsedTeamCourses';

import { DEFAULT_IMG_CONTENT } from '../../../constants';
import { SeeAllCard } from '../components/see-all-card';
import {
  ITEMS_CUSTOM,
  LOCATION_SESSION_PLACEHOLDER_IMAGE,
  VIRTUAL_SESSION_PLACEHOLDER_IMAGE,
} from '../constants';
import type { GetHomeUserCoursesWithoutTrack_me_enrollmentSet_edges_node_course as TCoursesWithoutTrack } from '../graphql/types/GetHomeUserCoursesWithoutTrack';
import useTranslations from '../hooks/useTranslations';

type TReturnParse = Pick<
  TInteractiveCourseCardProps,
  | 'title'
  | 'duration'
  | 'courseId'
  | 'coverImage'
  | 'level'
  | 'onClick'
  | 'expiresDate'
  | 'isLiveSessionCourse'
  | 'typeLiveSession'
  | 'dateSession'
  | 'hourSession'
  | 'isRequired'
> & { coins?: number };

const useCardHandler = (courseCoins?: number, tracksCoins?: number) => {
  const { t, languageCode } = useTranslations();

  const {
    baseUrlB2B = '',
    redirectTo,
    organizationSlug,
  } = useLearnDashboardContext();

  const redirectToTrack = useCallback(
    (team: UserTeamItem) => {
      const path = `${baseUrlB2B}/my-courses/tracks/${team.teamId}/`;
      redirectTo(path);
    },
    [baseUrlB2B, redirectTo],
  );

  const redirectToCourse = (courseOriginalId: number) => {
    const path = `/clases/v2/${courseOriginalId}/detalle`;

    window.location.href = path;
  };

  const goToLiveSession = (sessionId: number) => {
    const baseUrl = window.location.origin;

    window.open(
      `${baseUrl}/org/${organizationSlug}/live-sessions/${sessionId}/`,
      '_blank',
    );
  };

  const redirectToCatalog = () => {
    const url = createUrlWithSourceQueryParams({
      url: baseUrlB2B + '/content/',
      source: 'Home',
      sourceDetail: 'Text Link Tracks',
    });

    redirectTo(url);
  };

  const generateCardButtons = (
    items: TReturnParse[] = [],
    titleContent?: string,
    onActionShowMore?: () => void,
  ) => {
    const titleSessions = titleContent === t('SHOW_MORE_LIVE_SESSIONS');
    if (!items) {
      return [];
    }

    const cardItems: React.ReactNode[] = items.map(
      ({
        title,
        duration,
        courseId,
        coverImage,
        level,
        onClick,
        coins,
        expiresDate,
        isLiveSessionCourse,
        typeLiveSession,
        dateSession,
        hourSession,
        isRequired,
      }) => {
        const EXPIRE_DAYS = isDateValid(expiresDate)
          ? formatDate(new Date(expiresDate!), {
              outputFormat: 'D [de] MMMM',
              languageCode,
            })
          : undefined;

        return (
          <CourseCard
            key={`slides_card_${courseId}`}
            title={title}
            className="tw-h-full tw-w-full md:tw-min-w-full tw-cursor-pointer"
            duration={duration}
            courseId={courseId}
            coverImage={coverImage}
            level={level}
            // Covers are served at 16:9 and painted with object-cover, so a
            // fixed near-square height re-crops and upscales them. Full width
            // + 16:9 keeps the track/course art intact on every screen size.
            sizeImage="tw-h-auto tw-aspect-video"
            onClick={onClick}
            coinsNumber={coins}
            isLiveSessionCourse={isLiveSessionCourse}
            typeLiveSession={typeLiveSession}
            hourSession={hourSession}
            dateSession={dateSession}
            expiresDate={
              EXPIRE_DAYS &&
              t('EXPIRE_DATE', {
                interpolation: {
                  date: EXPIRE_DAYS,
                },
              })
            }
            isRequired={isRequired}
          />
        );
      },
    );

    cardItems.push(
      <SeeAllCard
        key={`slides_card_${cardItems.length}`}
        titleCard={titleContent}
        titleButton={t(
          titleSessions ? 'SEE_ALL_LINK_SESSIONS' : 'SEE_ALL_LINK',
        )}
        onActionShowMore={onActionShowMore}
      />,
    );

    return cardItems;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentionally managed
  const parseItems = useCallback(
    ({
      itemsParsedCourse,
      itemsParsedTeams,
      itemsParsedLiveSessions,
    }: {
      itemsParsedCourse?: TCoursesWithoutTrack[];
      itemsParsedTeams?: UserTeamItemParsed[];
      itemsParsedLiveSessions?: TSession[];
    }): TReturnParse[] | undefined => {
      if (itemsParsedTeams) {
        return itemsParsedTeams.slice(0, ITEMS_CUSTOM).map(items => {
          return {
            title: items.team?.name || '',
            courseId: items.originalId?.toString() || '',
            coverImage:
              items?.team?.teamImageV2?.url ||
              items?.team?.teamImageV2?.placeholder ||
              DEFAULT_IMG_CONTENT,
            onClick: () => redirectToTrack(items),
            coins: tracksCoins,
            expiresDate: items.expirationDate || undefined,
          };
        });
      }

      if (itemsParsedCourse) {
        return itemsParsedCourse.slice(0, ITEMS_CUSTOM).map(items => {
          return {
            title: items.title,
            courseId: items.originalId?.toString() || '',
            coverImage: items?.promoImage || items?.imagePlaceholder || '',
            duration: items.duration || 0,
            level: items.level?.name,
            onClick: () => redirectToCourse(items.originalId || 0),
            coins: courseCoins,
            isRequired: items.studioConfigSet?.isMandatory || false,
          };
        });
      }

      //TODO: Improve Type for LiveSession when Data in PROD
      if (itemsParsedLiveSessions) {
        const itemsCustom = itemsParsedLiveSessions;

        return itemsCustom.slice(0, ITEMS_CUSTOM).map(items => {
          const coverImageEmpty =
            items.type === 'virtual'
              ? VIRTUAL_SESSION_PLACEHOLDER_IMAGE
              : LOCATION_SESSION_PLACEHOLDER_IMAGE;

          const startHour = items.startDate.toLocaleTimeString(languageCode, {
            hour12: true,
            hour: 'numeric',
            minute: '2-digit',
          });

          const endHour = items.endDate.toLocaleTimeString(languageCode, {
            hour12: true,
            hour: 'numeric',
            minute: '2-digit',
          });

          const dateSession = formatDate(items.startDate, {
            languageCode,
            outputFormat: 'DD/MM/YYYY',
          });

          return {
            title: items.title,
            courseId: items.id?.toString() || '',
            coverImage: items?.imageUrl || coverImageEmpty,
            duration: 0,
            level: '',
            onClick: () => goToLiveSession(Number(items.id) || 0),
            isLiveSessionCourse: true,
            typeLiveSession: items.type,
            dateSession,
            hourSession: `${startHour} - ${endHour}`,
          };
        });
      }
    },
    [courseCoins, redirectToTrack, tracksCoins],
  );

  return { generateCardButtons, parseItems, redirectToCatalog };
};

export default useCardHandler;
